import { isValidElement, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import * as spriteIcons from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import { RadioButton, SwitchButton } from 'components-ref/atoms'
import { Icons } from 'helpers/icons'

import {
    Arrow,
    Avatar,
    Body,
    Buttons,
    Description,
    DropDownContent,
    IconWrapper,
    PendingNumber,
    SelectIcon,
    SelectIconWrapper,
    StyledActionCard,
    Subtext,
    Suptext,
    Text,
    TopArea,
    Wire
} from './ActionCard.styled'
import { type IActionCardProps } from './ActionCard.types'

export const ActionCard = ({
    icon,
    text,
    isEmphasizedText,
    suptext,
    subtext,
    description,
    selected,
    bottomArea,
    trashFn,
    editFn,
    clickFn,
    iconSize,
    toggleActive,
    toggleFn,
    hasRadio,
    hasSwitch,
    hasArrow,
    hasCheckmark,
    hasCheckmarkFn,
    onArrowClick,
    absFix,
    addNewText,
    disabled,
    inputId,
    link,
    pendingNumber,
    avatar,
    customHtml,
    wireText,
    linkInArrow,
    customStyle,
    switchText,
    dropDownContent,
    isDropdownOpen
}: IActionCardProps) => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [dropDownOpen, setDropDownOpen] = useState(false)
    useEffect(() => {
        if (isDropdownOpen) {
            setDropDownOpen(true)
        } else {
            setDropDownOpen(false)
        }
    }, [isDropdownOpen])


    const avatarEl = (() => {
    if (isValidElement(avatar) && avatar.type === 'img') {
        const props = (avatar.props || {}) as any
        const rawSrc = typeof props.src === 'string' ? props.src.trim() : ''
        const normalizedSrc = rawSrc || avatarPlaceholder

        return (
        <img
            {...props}
            src={normalizedSrc}
            alt={props.alt ?? t('avatarPlaceholder')}
        />
        )
    }
    // If caller passed a URL string, create an <img>.
    if (typeof avatar === 'string') {
        const src = avatar.trim()
        return src ? <img src={src} alt={t('avatarPlaceholder')} /> : null
    }
    // If caller passed some other element, render it as-is.
    if (avatar) return avatar

    return null
    })()
    
    return (
        <StyledActionCard
            $disabled={disabled}
            $selected={selected}
            $customStyle={customStyle}
            $addNewText={addNewText}
            onClick={(e: any) => {
                if (link && !linkInArrow && link !== 'undefined') {
                    navigate(link)
                }
                clickFn && clickFn(e)
            }}
        >
            {selected && (
                <SelectIconWrapper>
                    <SelectIcon>
                        <spriteIcons.IconCheckmark />
                    </SelectIcon>
                </SelectIconWrapper>
            )}
            <TopArea>
                {avatarEl && <Avatar>{avatarEl}</Avatar>}
                {icon && (
                    <IconWrapper
                        $absFix={absFix}
                        $iconSize={iconSize}
                        $addNewText={addNewText}
                    >
                        {typeof icon === 'string' ? (
                            <img src={icon} alt={t('actionIcon')} />
                        ) : (
                            icon
                        )}
                    </IconWrapper>
                )}
                <Body>
                    {suptext && <Suptext>{suptext}</Suptext>}
                    {text && (
                        <Text
                            $disabled={disabled}
                            $addNewText={addNewText}
                            $isEmphasizedText={isEmphasizedText && dropDownOpen}
                        >
                            {text}
                        </Text>
                    )}
                    {subtext && <Subtext>{subtext}</Subtext>}
                    {description && typeof description !== 'function' && (
                        <Description>{description}</Description>
                    )}
                </Body>
                {customHtml && <div>{customHtml}</div>}
                <Buttons>
                    {pendingNumber ? (
                        <PendingNumber>{pendingNumber}</PendingNumber>
                    ) : null}
                    {!!trashFn && (
                        <IconButton
                            icon={
                                <spriteIcons.IconTrashcan
                                    width='18'
                                    height='20'
                                />
                            }
                            type='white'
                            size='medium'
                            clickFn={trashFn}
                        />
                    )}
                    {!!editFn && (
                        <IconButton
                            icon={<spriteIcons.IconEdit color='#B0B0B0' />}
                            type='white'
                            size='medium'
                            clickFn={editFn}
                        />
                    )}
                    {hasRadio && (!!toggleFn || !!clickFn) && (
                        <>
                            <input type='checkbox' id={inputId} hidden />
                            <RadioButton
                                active={toggleActive}
                                clickFn={toggleFn}
                            />
                        </>
                    )}
                    {hasSwitch && !!toggleFn && (
                        <SwitchButton active={toggleActive} toggle={toggleFn} />
                    )}
                    {switchText && !!toggleFn && (
                        <SwitchButton
                            active={toggleActive}
                            toggle={toggleFn}
                            text={switchText}
                        />
                    )}
                    {wireText && <Wire>{wireText}</Wire>}
                    {dropDownContent && (
                        <Arrow
                            $rotate={dropDownOpen}
                            $isEmphasizedText={isEmphasizedText && dropDownOpen}
                            onClick={() => {
                                setDropDownOpen(!dropDownOpen)
                            }}
                        >
                            <spriteIcons.IconDownChevron width='15' />
                        </Arrow>
                    )}
                    {hasArrow && (
                        <Arrow
                            onClick={() => {
                                if (link && link !== 'undefined') {
                                    navigate(link)
                                }
                                onArrowClick && onArrowClick()
                            }}
                        >
                            <spriteIcons.IconRightChevron />
                        </Arrow>
                    )}
                    {hasCheckmark && (
                        <IconButton
                            icon={Icons.checkmark_outline_blue}
                            type='white'
                            size='medium'
                            clickFn={clickFn}
                        />
                    )}
                    {!!hasCheckmarkFn && (
                        <IconButton
                            icon={Icons.checkmark_outline_blue}
                            type='white'
                            size='medium'
                            clickFn={hasCheckmarkFn}
                        />
                    )}
                </Buttons>
            </TopArea>
            {bottomArea && <div>{bottomArea}</div>}
            {dropDownContent && (
                <DropDownContent $dropDownOpen={dropDownOpen}>
                    {dropDownContent}
                </DropDownContent>
            )}
        </StyledActionCard>
    )
}
